import {createCssProperty, removeCssProperty} from '@aboutyou/utils';
import React, {useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';

const HEADER_HEIGHT = 34;
export const COUPON_SLIDER_HEIGHT_CSS_VAR_NAME = '--coupon-slider-height';
export const QUICK_VIEW_PRODUCT_SLIDER_PROGRESS_BAR_DISPLAY =
  '--slider-progress-bar-display';

export interface CompactSlideInContainerProps {
  ['data-testid']?: string;
  className?: string;
  HeaderContent: React.ReactNode;
  onToggle?: (isOpen: boolean) => void;
  removeNegativeMarginTop?: boolean;
}

export const CompactSlideInContainer = ({
  'data-testid': dataTestId,
  children,
  className,
  HeaderContent,
  onToggle,
  removeNegativeMarginTop,
  ref,
}: React.PropsWithChildren<CompactSlideInContainerProps> & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    createCssProperty(COUPON_SLIDER_HEIGHT_CSS_VAR_NAME, `${HEADER_HEIGHT}px`);
    createCssProperty(QUICK_VIEW_PRODUCT_SLIDER_PROGRESS_BAR_DISPLAY, 'none');

    return () => {
      removeCssProperty(COUPON_SLIDER_HEIGHT_CSS_VAR_NAME);
      removeCssProperty(QUICK_VIEW_PRODUCT_SLIDER_PROGRESS_BAR_DISPLAY);
    };
  }, []);

  useEffect(() => {
    if (contentRef.current) {
      if (isOpen) {
        contentRef.current.removeAttribute('inert');
      } else {
        contentRef.current.setAttribute('inert', '');
      }
    }
  }, [isOpen, contentRef]);

  const handleOnClick = () => {
    onToggle?.(!isOpen);
    setIsOpen(prevState => !prevState);
  };

  return (
    <Wrapper
      ref={ref}
      data-testid={dataTestId}
      className={className}
      data-is-open={isOpen}
      $contentHeight={contentRef.current?.offsetHeight}
      $removeNegativeMarginTop={removeNegativeMarginTop}
    >
      <Header
        role="button"
        onClick={handleOnClick}
        data-testid={`${dataTestId}Header`}
        tabIndex={0}
        aria-label={undefined}
      >
        {HeaderContent}
      </Header>
      <div ref={contentRef}>{children}</div>
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  $contentHeight?: number;
  $removeNegativeMarginTop?: boolean;
}>`
  width: 100%;
  display: flex;
  height: ${HEADER_HEIGHT}px;
  flex-direction: column;
  background-color: transparent;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out;

  ${({$removeNegativeMarginTop}) =>
    !$removeNegativeMarginTop &&
    css`
      margin-top: -${HEADER_HEIGHT}px;
    `}

  &[data-is-open='true'] {
    ${({$contentHeight}) =>
      $contentHeight &&
      css`
        transform: translateY(-${$contentHeight}px);
      `}
  }
`;

const Header = styled(UnstyledButton)`
  height: ${HEADER_HEIGHT}px;
  cursor: pointer;
`;
